/**
 * SystemMenu — bare edit button for system playlists (Watch Later, Favorites).
 *
 * System playlists cannot be deleted, shared, or copy-linked, so a dropdown
 * menu with a single action is redundant. This component renders the edit
 * trigger directly. See `CustomMenu.tsx` for user-created playlists.
 *
 * Explicit variant rather than threading an `isSystemPlaylist` boolean
 * into a single component. Reuses the `profilePlaylistMenuOptions`
 * static-text slice and SCSS partial.
 *
 * **Static-text wiring**: this component does **not** mount its own
 * `connectStaticText` HOC + `<StaticTextProvider>`. The single, lifted
 * `<PlaylistMenuStaticTextScope>` in `ProfileMyPlaylists` provides the
 * `profilePlaylistMenuOptions` slice once for every menu instance — see
 * that file for the rationale.
 */

import React, { useCallback } from 'react'
import { useTranslatedText } from '../../../../StaticText/StaticTextContext'
import Button, { TYPES as BUTTON_TYPES } from '../../../../Button/index'
import Edit from '../../../../Icon/icons/Edit/index'

export interface SystemMenuProps {
  playlistId: string
  playlistType?: string
  playlistName?: string
  /** Invoked with (playlistId, playlistType?, playlistName?). */
  onEdit?: ((
    playlistId: string,
    playlistType?: string,
    playlistName?: string,
  ) => void) | null
}

function SystemMenu ({
  playlistId,
  playlistType,
  playlistName,
  onEdit = null,
}: SystemMenuProps) {
  const editPlaylistLabel = useTranslatedText({ keys: ['editPlaylist'] })

  const handleClick = useCallback(() => {
    if (onEdit) onEdit(playlistId, playlistType, playlistName)
  }, [onEdit, playlistId, playlistType, playlistName])

  return (
    <Button
      type={BUTTON_TYPES.LINK}
      className="profile-playlist-menu-options__trigger profile-playlist-menu-options__trigger--edit"
      onClick={handleClick}
      aria-label={editPlaylistLabel}
    >
      <Edit />
    </Button>
  )
}

export default React.memo(SystemMenu)
